import { useLayoutEffect, useState, type RefObject } from "react";

export interface ScrollOverflow {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

/**
 * Tracks whether a horizontally scrollable container is clipped on either
 * side, so the caller can render scroll affordances (edge fades). Listens to
 * scroll on the container and observes both the container and its content
 * for size changes. The 1px epsilon absorbs subpixel scrollLeft rounding at
 * the scroll extremes.
 */
export function useScrollOverflow(
  ref: RefObject<HTMLElement | null>,
): ScrollOverflow {
  const [state, setState] = useState<ScrollOverflow>({
    canScrollLeft: false,
    canScrollRight: false,
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = (): void => {
      const canScrollLeft = el.scrollLeft > 1;
      const canScrollRight =
        el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setState((prev) =>
        prev.canScrollLeft === canScrollLeft &&
        prev.canScrollRight === canScrollRight
          ? prev
          : { canScrollLeft, canScrollRight },
      );
    };

    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);

    return () => {
      el.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [ref]);

  return state;
}
